import { Button, Icon, Input, RichTextEditor } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import type { CardModalGridContent, CardModalGridCard } from "./types";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import styles from "../CMS.module.css";

type CardModalGridEditorProps = {
  value: CardModalGridContent;
  onChange: (value: CardModalGridContent) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

type CardModalGridFormValues = {
  cards: CardModalGridCard[];
};

function getNextId(cards: CardModalGridCard[]): number {
  if (cards.length === 0) return 1;
  return Math.max(...cards.map(c => c.id)) + 1;
}

export function CardModalGridEditor({
  value,
  onChange,
  onUploadingChange,
}: CardModalGridEditorProps) {
  const methods = useForm<CardModalGridFormValues>({
    defaultValues: { cards: value.cards ?? [] },
  });

  const { control, register, watch, getValues, setValue } = methods;
  const uploads = useRef(new Set<string>());
  const [uploading, setUploading] = useState(false);
  function reportUpload(key: string, pending: boolean) {
    if (pending) uploads.current.add(key);
    else uploads.current.delete(key);
    setUploading(uploads.current.size > 0);
    onUploadingChange?.(uploads.current.size > 0);
  }

  const { fields, append, remove, swap } = useFieldArray<
    CardModalGridFormValues,
    "cards",
    "fieldKey"
  >({
    control,
    name: "cards",
    keyName: "fieldKey",
  });

  function stripWrappingParagraph(html: string): string {
    const trimmed = html.trim();
    if (trimmed.startsWith("<p>") && trimmed.endsWith("</p>") && trimmed.indexOf("<p>", 1) === -1) {
      return trimmed.slice(3, -4);
    }
    return trimmed;
  }

  useEffect(() => {
    const subscription = watch(formValues => {
      if (formValues.cards) {
        const cleaned = (formValues.cards as CardModalGridCard[]).map(card => ({
          ...card,
          description: stripWrappingParagraph(card.description ?? ""),
        }));
        onChange({ ...value, cards: cleaned });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, value]);

  function handleAddCard() {
    const currentCards = watch("cards");
    const newCard: CardModalGridCard = {
      id: getNextId(currentCards),
      order: currentCards.length ? Math.max(...currentCards.map(card => card.order ?? 0)) + 1 : 0,
      heading: "",
      subheading: "",
      image: "",
      description: "",
      socialLink: "",
    };
    append(newCard);
  }

  function handleMoveCard(index: number, direction: "up" | "down") {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= fields.length) return;
    const orders = getValues("cards")
      .map(card => card.order)
      .sort((a, b) => a - b);
    swap(index, swapIndex);
    getValues("cards").forEach((_, i) => setValue(`cards.${i}.order`, orders[i]));
  }

  return (
    <FormProvider {...methods}>
      <div className={styles.cardEditorList}>
        {fields.map((field, index) => (
          <div key={field.fieldKey} className={styles.cardEditorItem}>
            <div className={styles.cardEditorItemHeader}>
              <span className={styles.cardEditorItemTitle}>Card {index + 1}</span>
              <div className={styles.cardEditorItemActions}>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => handleMoveCard(index, "up")}
                  disabled={uploading || index === 0}
                  aria-label={`Move card ${index + 1} up`}
                >
                  <Icon icon="chevron-up" size="small" />
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => handleMoveCard(index, "down")}
                  disabled={uploading || index === fields.length - 1}
                  aria-label={`Move card ${index + 1} down`}
                >
                  <Icon icon="chevron-down" size="small" />
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => remove(index)}
                  disabled={uploading}
                  aria-label={`Delete card ${index + 1}`}
                >
                  <Icon icon="delete" size="small" />
                </Button>
              </div>
            </div>

            <div className={styles.cardEditorFields}>
              <div className={styles.cardEditorFieldRow}>
                <div className={styles.formField}>
                  <label
                    className={styles.formLabel}
                    htmlFor={`card-modal-order-${field.fieldKey}`}
                  >
                    Order
                  </label>
                  <Input
                    id={`card-modal-order-${field.fieldKey}`}
                    type="number"
                    {...register(`cards.${index}.order`, { valueAsNumber: true })}
                  />
                </div>
              </div>

              <div className={styles.cardEditorFieldRow}>
                <div className={styles.formField}>
                  <label
                    className={styles.formLabel}
                    htmlFor={`card-modal-heading-${field.fieldKey}`}
                  >
                    Heading
                  </label>
                  <Input
                    id={`card-modal-heading-${field.fieldKey}`}
                    {...register(`cards.${index}.heading`)}
                  />
                </div>

                <div className={styles.formField}>
                  <label
                    className={styles.formLabel}
                    htmlFor={`card-modal-subheading-${field.fieldKey}`}
                  >
                    Subheading
                  </label>
                  <Input
                    id={`card-modal-subheading-${field.fieldKey}`}
                    {...register(`cards.${index}.subheading`)}
                  />
                </div>
              </div>

              <div className={styles.cardEditorFieldRow}>
                <div className={styles.formField}>
                  <Controller
                    name={`cards.${index}.image`}
                    control={control}
                    render={({ field: image }) => (
                      <CmsImageUploadField
                        value={image.value}
                        cardLabel={`Card ${index + 1}: ${watch(`cards.${index}.heading`) || "Untitled"}`}
                        onChange={image.onChange}
                        onUploadingChange={pending => reportUpload(field.fieldKey, pending)}
                      />
                    )}
                  />
                </div>

                <div className={styles.formField}>
                  <label
                    className={styles.formLabel}
                    htmlFor={`card-modal-social-${field.fieldKey}`}
                  >
                    Social Link (optional)
                  </label>
                  <Input
                    id={`card-modal-social-${field.fieldKey}`}
                    {...register(`cards.${index}.socialLink`)}
                  />
                </div>
              </div>

              <div className={styles.formField}>
                <Controller
                  name={`cards.${index}.description`}
                  control={control}
                  render={({ field: { value: descValue, onChange: descChange } }) => (
                    <RichTextEditor
                      id={`card-modal-description-${field.fieldKey}`}
                      value={descValue}
                      onChange={descChange}
                      preset="basic"
                      label={`Description for Card ${index + 1}`}
                      minHeight={120}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={handleAddCard}
          disabled={uploading}
        >
          <Icon icon="plus" size="small" />
          Add Card
        </Button>
      </div>
    </FormProvider>
  );
}
